import {
  CheckCircleOutline as CheckCircleOutlineIcon,
  CircleOutlined as CircleOutlinedIcon,
  DeleteTwoTone as DeleteTwoToneIcon,
  FilterList as FilterListIcon
} from '@mui/icons-material';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Tooltip,
  Typography
} from '@mui/material';
import {
  DataGrid,
  GridActionsCellItem,
  GridCellParams,
  GridColDef,
  GridRowSelectionModel,
  GridSortModel
} from '@mui/x-data-grid';
import { useAtom } from 'jotai';
import { useCallback, useEffect, useState, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { useCounterPartyController } from 'api/controllers/CounterPartyController';

import {
  Counterparty,
  CounterpartyFilters,
  CounterpartyQueryFilters,
  CounterpartySortColumns,
  CounterpartyType,
  SortOrder
} from 'openapi';

import { CounterPartiesFilterPanel } from 'components/CompanySettings/CounterParties/CounterPartiesFilterPanel';
import { CounterpartiesToolbar } from 'components/Counterparties/CounterpartiesToolbar';
import { OverflowTooltipCell } from 'components/shared/gridCells/OverflowTooltipCell';
import { Modal } from 'components/shared/Modal/Modal';

import { useTranslations } from 'context/TranslationContext';

import { useModal } from 'hooks/useModal';
import { usePagination } from 'hooks/usePagination';

import {
  ACTIONS,
  APPROVED,
  CHECK,
  DEFAULT_GRID_ROW_HEIGHT,
  DEFAULT_PAYMENT,
  EMPTY_CELL_VALUE,
  TYPE
} from 'utils/constants/constants';
import { INITIAL_COUNTERPARTY_FILTERS } from 'utils/constants/counterparty';
import { FilterKeys, PAGINATION_SORT_KEYS } from 'utils/constants/invoices';
import { INITIAL_PAGINATION_PROPS } from 'utils/constants/paginations';
import { CounterpartyFilterType } from 'utils/enums/Counterparty';
import {
  getCounterpartyRowId,
  getCounterpartyTypeLabel,
  getDefaultPaymentIban
} from 'utils/helpers/counterPartyHelper';
import { TranslateFunction } from 'utils/interfaces/Translations';
import { getMappedCounterParties } from 'utils/mappers/counterParty';
import { AppRoutesEnum } from 'utils/routes';

import { counterpartiesDataGridStyle } from 'styles/components/CounterpartiesStyle';
import { commonDataGridContainerStyle } from 'styles/components/DataGridStyle';
import { invoicesDataGrid } from 'styles/components/InvoicesDataGridStyle';
import { modalButtonsWrapper } from 'styles/pages/InvoiceVerificationStyle';

import { filterTypeAtom, counterpartiesGridStateAtom } from 'state/state';

export const BaseCounterparties = ({
  getColumns,
  isFilterable = false
}: {
  getColumns: (translate: TranslateFunction) => GridColDef[];
  isFilterable?: boolean;
}) => {
  const { companyId } = useParams();
  const navigate = useNavigate();
  const { translate } = useTranslations();

  const initialPage = INITIAL_PAGINATION_PROPS.page;
  const initialPageSize = INITIAL_PAGINATION_PROPS.pageSize;
  const [totalCounterParties, setTotalCounterParties] = useState<number>(0);

  const [gridStates, setGridStates] = useAtom(counterpartiesGridStateAtom);
  const storedGridState = companyId ? gridStates[companyId] : undefined;

  const { paginationModel, handlePaginationModelChange } = usePagination({
    page: storedGridState?.paginationModel.page ?? initialPage,
    pageSize: storedGridState?.paginationModel.pageSize ?? initialPageSize,
    rowCount: totalCounterParties
  });

  const {
    isOpen: isDeleteModalOpen,
    openModal: openDeleteModal,
    closeModal: closeDeleteModal
  } = useModal();

  const {
    deleteCounterParties,
    getFilteredCounterpartiesByCompanyId,
    getCounterpartyFilters
  } = useCounterPartyController();

  const [filterType] = useAtom(filterTypeAtom);

  const [counterParties, setCounterParties] = useState<Counterparty[]>();
  const [selectedRows, setSelectedRows] = useState<GridRowSelectionModel>([]);
  const [filters, setFilters] = useState<CounterpartyQueryFilters>(
    storedGridState?.filters ?? INITIAL_COUNTERPARTY_FILTERS
  );
  const [filtersData, setFiltersData] = useState<CounterpartyFilters>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [areFiltersVisible, setAreFiltersVisible] = useState<boolean>(false);
  const [selectedSingleCounterPartyId, setSelectedSingleCounterPartyId] =
    useState<number>();

  const handleSortModelChange = useCallback((sortModel: GridSortModel) => {
    const hasSortModel = !!sortModel.length;
    const { field, sort } = hasSortModel
      ? sortModel[0]
      : { field: '', sort: '' };

    setFilters((prevFilters) =>
      hasSortModel
        ? {
            ...prevFilters,
            sortBy: field as CounterpartySortColumns,
            sortOrder: sort?.toUpperCase() as SortOrder
          }
        : { ...prevFilters, ...INITIAL_COUNTERPARTY_FILTERS }
    );
  }, []);

  const fetchCounterParties = useCallback(
    async (data: CounterpartyQueryFilters) => {
      setIsLoading(true);
      const result = await getFilteredCounterpartiesByCompanyId(
        Number(companyId),
        {
          ...data,
          pageNumber: paginationModel.page + 1,
          pageSize: paginationModel.pageSize,
          sortBy: data.sortBy,
          sortOrder: data.sortOrder?.toUpperCase() as SortOrder,
          isValidated:
            filterType === CounterpartyFilterType.NotValidated
              ? false
              : data.isValidated
        }
      );

      const mappedCounterparties = getMappedCounterParties(
        result.counterparties
      );

      setTotalCounterParties(result.totalElements);
      setCounterParties(mappedCounterparties);
      setIsLoading(false);
    },
    [
      getFilteredCounterpartiesByCompanyId,
      paginationModel,
      companyId,
      filterType
    ]
  );

  const fetchCounterPartyFilters = useCallback(async () => {
    const data = await getCounterpartyFilters(Number(companyId));
    setFiltersData(data);
  }, [getCounterpartyFilters, companyId]);

  useEffect(() => {
    fetchCounterPartyFilters();
  }, []);

  useEffect(() => {
    fetchCounterParties(filters);
  }, [paginationModel, filters, filterType]);

  useEffect(() => {
    if (!companyId) return;

    setGridStates((previousStates) => ({
      ...previousStates,
      [companyId]: { filters, paginationModel }
    }));
  }, [companyId, filters, paginationModel, setGridStates]);

  const handleAddClick = () => {
    if (!companyId) return;

    navigate(
      AppRoutesEnum.COMPANY_COUNTERPARTIES_NEW.replace(':companyId', companyId)
    );
  };

  const handleEditClick = (params: GridCellParams) => {
    if (params.field === CHECK || params.field === ACTIONS) return;
    if (!companyId) return;

    const rowId = getCounterpartyRowId(params.row);
    if (!rowId) return;

    navigate(
      AppRoutesEnum.COMPANY_COUNTERPARTIES_EDIT.replace(
        ':companyId',
        companyId
      ).replace(':counterpartyId', String(rowId))
    );
  };

  const handleCloseModals = () => {
    setSelectedSingleCounterPartyId(undefined);
    closeDeleteModal();
  };

  const handleDeleteClick = async () => {
    await deleteCounterParties(
      Number(companyId),
      selectedSingleCounterPartyId
        ? [selectedSingleCounterPartyId]
        : (selectedRows as number[])
    );
    fetchCounterParties(filters);
    fetchCounterPartyFilters();
    handleCloseModals();
  };

  const shouldShowMultiSelectInfo = useMemo(() => {
    return (
      selectedRows.length > 1 && selectedSingleCounterPartyId === undefined
    );
  }, [selectedRows, selectedSingleCounterPartyId]);

  const columns = useMemo(
    () =>
      getColumns(translate).map((column: GridColDef) => {
        if (column.field === TYPE) {
          return {
            ...column,
            renderCell: ({ row }: GridCellParams) => {
              const typeLabel = getCounterpartyTypeLabel(row.type, translate);

              if (!typeLabel) {
                return EMPTY_CELL_VALUE;
              }

              const isReimbursement =
                row.type === CounterpartyType.REIMBURSEMENT;

              return (
                <Chip
                  label={typeLabel}
                  size="small"
                  sx={{
                    height: 24,
                    fontSize: 12,
                    fontWeight: 600,
                    borderRadius: '3px',
                    '& .MuiChip-label': { px: '8px' },
                    ...(isReimbursement
                      ? {
                          bgcolor: '#f0fdf4',
                          color: '#15803d',
                          border: '1px solid #15803d33'
                        }
                      : {
                          bgcolor: '#eff6ff',
                          color: '#1e40af',
                          border: '1px solid #1e40af33'
                        })
                  }}
                />
              );
            }
          };
        }
        if (column.field === APPROVED) {
          return {
            ...column,
            renderCell: ({ row }: GridCellParams) => {
              const isApproved = !!row.isValidated;

              return (
                <Box display="flex" alignItems="center" gap={0.75} height={24}>
                  {isApproved ? (
                    <CheckCircleOutlineIcon
                      sx={{ fontSize: 20, color: '#15803d' }}
                    />
                  ) : (
                    <CircleOutlinedIcon
                      sx={{ fontSize: 20, color: '#d1d5db' }}
                    />
                  )}
                  <Typography
                    component="span"
                    sx={{
                      fontSize: 12,
                      lineHeight: 1.2,
                      color: isApproved ? '#15803d' : '#9ca3af',
                      fontWeight: isApproved ? 600 : 400
                    }}
                  >
                    {translate(
                      isApproved ? 'labels.approved' : 'labels.notApproved'
                    )}
                  </Typography>
                </Box>
              );
            }
          };
        }
        if (column.field === CounterpartySortColumns.EXPENSE_TYPE) {
          return {
            ...column,
            renderCell: ({ row }: GridCellParams) => {
              const expenseTypes = (row.expenseTypes ?? [])
                .map((expenseType: { name?: string }) => expenseType?.name)
                .filter(Boolean) as string[];

              if (!expenseTypes.length) {
                return EMPTY_CELL_VALUE;
              }

              const visibleExpenseTypes = expenseTypes.slice(0, 2);
              const remainingExpenseTypes =
                expenseTypes.length - visibleExpenseTypes.length;

              const expenseTypesCell = (
                <Box
                  display="flex"
                  alignItems="center"
                  gap={0.5}
                  overflow="hidden"
                  width="100%"
                >
                  {visibleExpenseTypes.map((expenseType) => (
                    <Chip
                      key={expenseType}
                      label={expenseType}
                      size="small"
                      sx={{
                        height: 22,
                        fontSize: 12,
                        fontWeight: 600,
                        bgcolor: 'rgba(7, 141, 238, 0.08)',
                        color: '#078DEE',
                        borderRadius: '3px',
                        '& .MuiChip-label': { px: '7px' }
                      }}
                    />
                  ))}
                  {remainingExpenseTypes > 0 && (
                    <Chip
                      label={`+${remainingExpenseTypes}`}
                      size="small"
                      sx={{
                        height: 22,
                        fontSize: 12,
                        bgcolor: '#f3f4f6',
                        color: '#6b7280',
                        borderRadius: '3px',
                        '& .MuiChip-label': { px: '7px' }
                      }}
                    />
                  )}
                </Box>
              );

              if (remainingExpenseTypes === 0) {
                return expenseTypesCell;
              }

              return (
                <Tooltip
                  placement="top"
                  slotProps={{ tooltip: { sx: { maxWidth: 360 } } }}
                  title={
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 0.25,
                        maxHeight: 240,
                        overflowY: 'auto',
                        fontSize: 12
                      }}
                    >
                      {expenseTypes.map((expenseType, index) => (
                        <Box key={`${expenseType}-${index}`}>{expenseType}</Box>
                      ))}
                    </Box>
                  }
                >
                  {expenseTypesCell}
                </Tooltip>
              );
            }
          };
        }
        if (column.field === DEFAULT_PAYMENT) {
          return {
            ...column,
            renderCell: ({ row }: GridCellParams) => (
              <OverflowTooltipCell value={getDefaultPaymentIban(row.ibans)} />
            )
          };
        }
        if (column.field === ACTIONS) {
          return {
            ...column,
            renderCell: ({ row }: GridCellParams) => (
              <Tooltip
                placement="left"
                title={
                  !row.isDeletable
                    ? translate('messages.disabledDeleteCounterparty')
                    : ''
                }
              >
                <span>
                  <GridActionsCellItem
                    disabled={!row.isDeletable}
                    icon={
                      <DeleteTwoToneIcon
                        sx={(theme) => ({
                          color: row.isDeletable
                            ? theme.palette.error.main
                            : 'default',
                          fontSize: '1.5rem'
                        })}
                        titleAccess={translate('labels.delete')}
                      />
                    }
                    label="Delete"
                    onClick={() => {
                      setSelectedSingleCounterPartyId(
                        getCounterpartyRowId(row)
                      );
                      openDeleteModal();
                    }}
                  />
                </span>
              </Tooltip>
            )
          };
        }
        return column;
      }),
    [translate, getColumns]
  );

  const handleApplyFilters = (data: CounterpartyQueryFilters) => {
    setFilters((previousFilters) => ({ ...previousFilters, ...data }));
    setAreFiltersVisible(false);
  };

  const handleShowFilters = () => {
    setAreFiltersVisible((previousValue) => !previousValue);
  };

  const selectedCounterparties = useMemo(
    () =>
      (counterParties ?? []).filter((counterparty) =>
        selectedRows.includes(getCounterpartyRowId(counterparty))
      ),
    [counterParties, selectedRows]
  );
  const noRowsSelected = selectedRows.length === 0;
  const shouldDisableDelete = selectedCounterparties.some(
    ({ isDeletable }) => !isDeletable
  );
  const deleteTooltip = noRowsSelected
    ? translate('warnings.noSelectedRows')
    : shouldDisableDelete
      ? translate('messages.disabledDeleteCounterparties')
      : '';

  const areFiltersApplied = useMemo(() => {
    const filtersAsRecord = filters as Record<string, unknown>;
    const initialFiltersAsRecord = INITIAL_COUNTERPARTY_FILTERS as Record<
      string,
      unknown
    >;

    return Object.keys(filters).some((key) => {
      if (PAGINATION_SORT_KEYS.has(key as FilterKeys)) {
        return false;
      }

      if (Array.isArray(filtersAsRecord[key])) {
        return (filtersAsRecord[key] as unknown[]).length > 0;
      }

      return filtersAsRecord[key] !== initialFiltersAsRecord[key];
    });
  }, [filters]);

  return counterParties ? (
    <Stack sx={{ height: '100%' }}>
      {isFilterable && (
        <Box display="flex" justifyContent="flex-end">
          <Button
            sx={{ width: '10%' }}
            variant={areFiltersApplied ? 'contained' : 'outlined'}
            color="primary"
            onClick={handleShowFilters}
          >
            <FilterListIcon />
            {translate('buttons.filters')}
          </Button>
        </Box>
      )}
      {isFilterable && (
        <CounterPartiesFilterPanel
          handleApplyFilters={handleApplyFilters}
          filtersData={filtersData}
          areFiltersVisible={areFiltersVisible}
          isNotApprovedView={filterType === CounterpartyFilterType.NotValidated}
        />
      )}
      <Paper
        elevation={4}
        sx={{
          ...commonDataGridContainerStyle,
          height: 'auto',
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          marginTop: '1rem'
        }}
      >
        <DataGrid
          rows={counterParties}
          columns={columns}
          getRowId={getCounterpartyRowId}
          loading={isLoading}
          checkboxSelection
          rowHeight={DEFAULT_GRID_ROW_HEIGHT}
          onCellClick={handleEditClick}
          disableRowSelectionOnClick
          sx={{
            ...invoicesDataGrid,
            ...counterpartiesDataGridStyle,
            flex: 1,
            minHeight: 0
          }}
          paginationMode="server"
          sortingMode="server"
          onRowSelectionModelChange={setSelectedRows}
          paginationModel={paginationModel}
          pageSizeOptions={[]}
          onPaginationModelChange={handlePaginationModelChange}
          rowCount={totalCounterParties}
          onSortModelChange={handleSortModelChange}
          slots={{
            toolbar: CounterpartiesToolbar as any
          }}
          slotProps={{
            toolbar: {
              deleteDisabled: noRowsSelected || shouldDisableDelete,
              deleteTooltip,
              onAdd: handleAddClick,
              onDelete: openDeleteModal
            } as any
          }}
          localeText={{
            noRowsLabel: translate('labels.noData')
          }}
        />
        {isDeleteModalOpen && (
          <Modal
            headerTitle={translate('titles.delete')}
            isOpen={isDeleteModalOpen}
            hide={handleCloseModals}
            maxWidth="sm"
          >
            <Typography variant="body1" mb={2}>
              {shouldShowMultiSelectInfo
                ? translate('messages.deleteCounterpartiesConfirmation', {
                    numberOfCounterparties: selectedRows.length.toString()
                  })
                : translate('messages.deleteCounterpartyConfirmation')}
            </Typography>
            <Box sx={modalButtonsWrapper}>
              <Button variant="outlined" onClick={handleCloseModals}>
                {translate('buttons.cancel')}
              </Button>
              <Button variant="contained" onClick={handleDeleteClick}>
                {translate('buttons.delete')}
              </Button>
            </Box>
          </Modal>
        )}
      </Paper>
    </Stack>
  ) : (
    <Stack alignItems="center" justifyContent="center" height="100%">
      <CircularProgress />
    </Stack>
  );
};
